import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { upsertUser } from '../lib/userApi';
import type { AuthContextType } from '../types/auth';
import type { CreateUserRequest, PublicUserRole, UserProfile, UserRole } from '../types/user';

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const PENDING_ROLE_KEY = 'mcc.pendingRole';

function getCognitoLogoutUrl(): string {
  const domain = process.env.REACT_APP_COGNITO_DOMAIN;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

  if (!domain || !clientId) {
    throw new Error('Cognito logout configuration is missing');
  }

  const logoutUrl = new URL('/logout', domain);
  logoutUrl.searchParams.set('client_id', clientId);
  logoutUrl.searchParams.set('logout_uri', window.location.origin);
  return logoutUrl.toString();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const oidc = useOidcAuth();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const profileRequestIdRef = useRef(0);
  const isSigningOutRef = useRef(false);
  const logoutPromiseRef = useRef<Promise<void> | null>(null);
  const accessToken = oidc.user?.access_token;

  const refreshUser = useCallback(async () => {
    const requestId = ++profileRequestIdRef.current;

    if (!accessToken) {
      if (!isSigningOutRef.current) {
        setUser(null);
        setProfileLoading(false);
        setError(null);
      }
      return;
    }

    if (isSigningOutRef.current) return;

    setProfileLoading(true);
    setError(null);

    try {
      const pendingRole = sessionStorage.getItem(PENDING_ROLE_KEY);
      const body: CreateUserRequest = {
        ...(pendingRole === 'caregiver' || pendingRole === 'client' ? { role: pendingRole } : {}),
      };
      const profile = await upsertUser(accessToken, body);

      if (requestId !== profileRequestIdRef.current || isSigningOutRef.current) return;

      setUser(profile);
      sessionStorage.removeItem(PENDING_ROLE_KEY);
    } catch (reason) {
      if (requestId === profileRequestIdRef.current && !isSigningOutRef.current) {
        setError(reason instanceof Error ? reason.message : 'Unable to load your account');
      }
    } finally {
      if (requestId === profileRequestIdRef.current && !isSigningOutRef.current) {
        setProfileLoading(false);
      }
    }
  }, [accessToken]);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  const login = useCallback(() => {
    sessionStorage.removeItem(PENDING_ROLE_KEY);
    return oidc.signinRedirect();
  }, [oidc]);

  const register = useCallback(async (role: PublicUserRole) => {
    sessionStorage.setItem(PENDING_ROLE_KEY, role);
    setError(null);

    try {
      await oidc.signinRedirect({ extraQueryParams: { screen_hint: 'signup' } });
    } catch (reason) {
      sessionStorage.removeItem(PENDING_ROLE_KEY);
      setError(reason instanceof Error ? reason.message : 'Unable to start secure registration');
      throw reason;
    }
  }, [oidc]);

  const logout = useCallback((): Promise<void> => {
    if (logoutPromiseRef.current) return logoutPromiseRef.current;

    let logoutUrl: string;
    try {
      logoutUrl = getCognitoLogoutUrl();
    } catch (reason) {
      return Promise.reject(reason);
    }

    isSigningOutRef.current = true;
    profileRequestIdRef.current += 1;
    setIsSigningOut(true);
    setError(null);

    const request = Promise.resolve()
      .then(() => oidc.removeUser())
      .then(() => {
        sessionStorage.removeItem(PENDING_ROLE_KEY);
        setUser(null);
        window.location.replace(logoutUrl);
      })
      .catch((reason) => {
        isSigningOutRef.current = false;
        logoutPromiseRef.current = null;
        setIsSigningOut(false);
        setError(reason instanceof Error ? reason.message : 'Unable to sign out');
        if (!user) void refreshUser();
        throw reason;
      });

    logoutPromiseRef.current = request;
    return request;
  }, [oidc, refreshUser, user]);

  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated: oidc.isAuthenticated,
    isLoading: oidc.isLoading || profileLoading || isSigningOut,
    error: error ?? oidc.error?.message ?? null,
    login,
    register,
    logout,
    refreshUser,
    hasRole: (roles: UserRole | UserRole[]) => {
      const allowedRoles = Array.isArray(roles) ? roles : [roles];
      return Boolean(user && allowedRoles.includes(user.role));
    },
  }), [user, oidc, profileLoading, isSigningOut, error, login, register, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export { useOidcAuth };
